import { AxiosResponse } from 'axios';
import { useAxios } from '@/features/common/helpers/axios';
import { UploadDocument } from '@/features/shipment/types/documentType';

const { axiosService } = useAxios();

export interface SavedDocumentDto {
  id: number;
  name: string;
  documentType: string;
  originalFileName: string;
  fileExtension: string;
  mimeType: string;
  sizeBytes: number;
  createdUtc: string;
  updatedUtc: string | null;
}

export interface SaveToLibraryRequest {
  name: string;
  documentType: string;
  documentId: string;
}

export interface LibrarySortParams {
  orderBy?: string;
  orderByDescending?: boolean;
}

export async function getDocumentLibrary(
  sort: LibrarySortParams = { orderBy: 'lastUsedUtc', orderByDescending: true },
): Promise<SavedDocumentDto[]> {
  try {
    const response: AxiosResponse<SavedDocumentDto[]> = await axiosService.get(
      '/documents/library',
      { params: sort },
    );
    return response.data || [];
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('[ETD] Failed to fetch document library', error);
    return [];
  }
}

export async function saveToLibrary(
  request: SaveToLibraryRequest,
): Promise<SavedDocumentDto | null> {
  try {
    const response: AxiosResponse<SavedDocumentDto> = await axiosService.post(
      '/documents/library',
      request,
    );
    return response.data;
  } catch {
    return null;
  }
}

export async function deleteFromLibrary(id: number): Promise<boolean> {
  try {
    await axiosService.delete(`/documents/library/${id}`);
    return true;
  } catch {
    return false;
  }
}

export interface UpdateLibraryDocumentRequest {
  name: string;
  documentType: string;
}

export async function updateLibraryDocument(
  id: number,
  request: UpdateLibraryDocumentRequest,
): Promise<SavedDocumentDto | null> {
  try {
    const response: AxiosResponse<SavedDocumentDto> = await axiosService.put(
      `/documents/library/${id}`,
      request,
    );
    return response.data;
  } catch {
    return null;
  }
}

export interface UsmcaToggleResponse {
  includeUsmca: boolean;
}

/**
 * Enable or disable USMCA Certificate of Origin auto-generation on the current shipment.
 */
export async function toggleUsmca(enabled: boolean): Promise<UsmcaToggleResponse | null> {
  try {
    const response: AxiosResponse<UsmcaToggleResponse> = await axiosService.post(
      `/documents/usmca/autogenerate/${enabled}`,
    );
    return response.data;
  } catch {
    return null;
  }
}

/**
 * Fetches a PDF preview of the USMCA certificate for the current shipment.
 */
export async function previewUsmca(): Promise<Blob | null> {
  try {
    const response = await axiosService.get('/documents/usmca/preview', {
      params: { format: 'Pdf' },
      responseType: 'blob',
    });
    return response.data;
  } catch {
    return null;
  }
}

/**
 * Copies a library document's S3 file into the current shipment WIP folder.
 * Returns a real UploadDocument that can be viewed/downloaded/deleted.
 */
export async function attachFromLibrary(
  libraryDocumentId: number,
): Promise<UploadDocument | null> {
  try {
    const response: AxiosResponse<UploadDocument> = await axiosService.post(
      '/documents/attach-from-library',
      { libraryDocumentId },
    );
    return response.data;
  } catch {
    return null;
  }
}
